import type React from "react";

import { IconButton } from "@/components/Button/IconButton";
import { ThumbsDownIcon } from "@/components/Icons/ThumbsDownIcon";
import { ThumbsUpIcon } from "@/components/Icons/ThumbsUpIcon";
import { formatDistance } from "@/helpers/date";
import { useTranslation } from "@/translations";

export interface RatableItem {
  id: string;
  title: string;
  details: string[];
  rating?: "positive" | "negative";
  ratedAt?: string | Date;
}

export interface RatableSuggestionsListProps {
  items: RatableItem[];
  onRateItem: (itemId: string, rating: "positive" | "negative") => void;
}

export function RatableSuggestionsList({ items, onRateItem }: RatableSuggestionsListProps): React.ReactNode {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <ul className="flex list-decimal flex-col gap-6">
      {items.map((item, index) => (
        <SuggestionItemDisplay key={item.id} item={item} index={index} onRate={onRateItem} />
      ))}
    </ul>
  );

  interface SuggestionItemDisplayProps {
    item: RatableItem;
    index: number;
    onRate: (itemId: string, rating: "positive" | "negative") => void;
  }

  function SuggestionItemDisplay({ item, index, onRate }: SuggestionItemDisplayProps): React.ReactNode {
    const { t } = useTranslation();

    return (
      <li className="flex flex-col">
        <span className="text-body-bold">{`${index + 1}. ${item.title}`}</span>
        <ul className="flex list-disc flex-col gap-1 pl-8">
          {item.details.map((detail) => (
            <li key={detail} className="text-body">
              {detail}
            </li>
          ))}
        </ul>
        <div className="mt-2 flex h-10 items-center gap-2">
          <p className="flex-1 text-caption text-grey-700">
            {item.rating
              ? t("component.ratable-suggestion.received-feedback", {
                  date: item.ratedAt
                    ? formatDistance(t, {
                        start: new Date(item.ratedAt),
                        end: new Date(),
                      })
                    : "",
                })
              : t("component.ratable-suggestion.require-feedback")}
          </p>
          {!item.rating && (
            <>
              <IconButton
                icon={<ThumbsUpIcon />}
                title={t("component.ratable-suggestion.rate-helpful")}
                onClick={() => onRate(item.id, "positive")}
              />
              <IconButton
                icon={<ThumbsDownIcon />}
                title={t("component.ratable-suggestion.rate-not-helpful")}
                onClick={() => onRate(item.id, "negative")}
              />
            </>
          )}
        </div>
      </li>
    );
  }
}
